import { useState, type ComponentProps } from "react"

import { type Dialog } from "@/components/ui/dialog"

type ComponentWithDisplayName = React.ComponentType<never> & {
  displayName: string
}

type ExtractDisplayNames<T extends Record<string, ComponentWithDisplayName>> =
  T[keyof T] extends ComponentWithDisplayName
    ? T[keyof T]["displayName"]
    : never

export type DialogNames<T extends Record<string, ComponentWithDisplayName>> =
  ExtractDisplayNames<T>

export const useDialogs = <Dialog extends string>(): {
  isOpen: (dialog: Dialog) => boolean
  toggle: (dialog: Dialog) => () => void
  open: (dialog: Dialog) => void
  getProps: (
    dialog: Dialog
  ) => Pick<ComponentProps<typeof Dialog>, "open" | "onOpenChange">
} => {
  const [dialogs, setDialogs] = useState({} as Record<Dialog, boolean>)

  const isOpen = (dialog: Dialog) => {
    return !!dialogs[dialog]
  }

  const toggle = (dialog: Dialog) => () => {
    setDialogs(prev => ({ ...prev, [dialog]: !prev[dialog] }))
  }

  const open = (dialog: Dialog) => {
    setDialogs(prev => ({ ...prev, [dialog]: true }))
  }

  const onOpenChange = (dialog: Dialog) => (bool: boolean) => {
    setDialogs(prev => ({ ...prev, [dialog]: bool }))
  }

  const getProps = (dialog: Dialog) => {
    return {
      open: isOpen(dialog),
      onOpenChange: onOpenChange(dialog),
    }
  }

  return { isOpen, toggle, getProps, open }
}
